import { AuthorDTO } from '@/app/models/series.model';
import { Component, Input, OnInit, HostListener, ChangeDetectionStrategy } from '@angular/core';

@Component({
    selector: 'app-author-content-view',
    templateUrl: './author-content-view.component.html',
    styleUrls: ['./author-content-view.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AuthorContentViewComponent implements OnInit {

  @Input() author!: AuthorDTO;
  
  showModal: boolean = false;
  modalPosition = { top: 0, left: 0 };

  constructor() {}

  ngOnInit(): void {}

  // Toggle modal visibility
  toggleModal(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (this.showModal) {
      this.closeModal();
    } else {
      this.openModal(event);
    }
  }

  // Open modal at click position
  openModal(event: MouseEvent): void {
    this.showModal = true;
    
    // Calculate position relative to viewport for fixed positioning
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.modalPosition = {
      top: rect.bottom + 5,
      left: rect.left
    };

    // Adjust position if modal would go off screen
    setTimeout(() => {
      const modalElement = document.querySelector('.author-modal') as HTMLElement;
      if (modalElement) {
        const modalRect = modalElement.getBoundingClientRect();
        
        // Adjust horizontal position if too far right
        if (modalRect.right > window.innerWidth - 20) {
          this.modalPosition.left = window.innerWidth - modalRect.width - 20;
        }
        
        // Adjust vertical position if too far down
        if (modalRect.bottom > window.innerHeight - 20) {
          this.modalPosition.top = rect.top - modalRect.height - 5;
        }
        
        // Ensure modal doesn't go off left edge
        if (this.modalPosition.left < 10) {
          this.modalPosition.left = 10;
        }
        
        // Ensure modal doesn't go off top edge
        if (this.modalPosition.top < 10) {
          this.modalPosition.top = 10;
        }
      }
    }, 50);
  }

  // Close modal
  closeModal(): void {
    this.showModal = false;
  }

  // Close modal on escape key
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.showModal) {
      this.closeModal();
    }
  }

  // Get author full name with title
  getAuthorFullNameWithTitle(): string {
    if (!this.author) return '';
    if (this.author.title && this.author.title !== 'Yok') {
      return `${this.author.title} ${this.author.fullName}`.trim();
    }
    return this.author.fullName || '';
  }

  // Get author location string
  getAuthorLocation(): string {
    if (!this.author) return '';
    return [this.author.address, this.author.institution, this.author.city, this.author.country]
      .filter(Boolean)
      .join(', ');
  }

  // Check if author has location information
  hasLocationInfo(): boolean {
    if (!this.author) return false;
    return !!(this.author.address || this.author.institution || this.author.city || this.author.country);
  }

  // Open email client
  openEmail(): void {
    if (this.author && this.author.email) {
      window.open(`mailto:${this.author.email}`, '_blank');
    }
  }

  // Get author department info
  getDepartmentInfo(): string {
    if (!this.author) return '';
    return [this.author.department, this.author.institution]
      .filter(Boolean)
      .join(', ');
  }

  // Check if has department info
  hasDepartmentInfo(): boolean {
    if (!this.author) return false;
    return !!(this.author.department || this.author.institution);
  }

  // Get ORCID display format
  getOrcidDisplay(): string {
    if (!this.author || !this.author.orcid) return '';
    return this.author.orcid.replace('https://orcid.org/', '').replace('http://orcid.org/', '');
  }

  // Open ORCID profile
  openOrcid(): void {
    if (this.author && this.author.orcid) {
      const orcidUrl = this.author.orcid.startsWith('http') 
        ? this.author.orcid 
        : `https://orcid.org/${this.author.orcid}`;
      window.open(orcidUrl, '_blank');
    }
  }

  // Open website
  openWebsite(): void {
    if (this.author && this.author.website) {
      window.open(this.author.website, '_blank');
    }
  }
}
